/* Storefront video policy: no audio path is exposed by the player UI.
 * Original media remains untouched. Register before any deferred player starts.
 */
(() => {
  const registered = new WeakSet();
  const native = Object.fromEntries(['muted', 'defaultMuted', 'volume', 'controls'].map(key =>
    [key, Object.getOwnPropertyDescriptor(HTMLMediaElement.prototype, key)]));

  function protect(video) {
    if (!video.closest('ci-demo-video, deferred-media[data-ci-storefront-video]') && !video.hasAttribute('data-ci-storefront-video')) return;
    if (registered.has(video)) return;
    registered.add(video);
    const enforce = () => {
      if (!native.muted.get.call(video)) native.muted.set.call(video, true);
      if (!native.defaultMuted.get.call(video)) native.defaultMuted.set.call(video, true);
      if (native.volume.get.call(video) !== 0) native.volume.set.call(video, 0);
      if (native.controls.get.call(video)) native.controls.set.call(video, false);
      if (!video.hasAttribute('muted')) video.setAttribute('muted', '');
      if (!video.hasAttribute('playsinline')) video.setAttribute('playsinline', '');
      video.disablePictureInPicture = true;
      video.disableRemotePlayback = true;
    };
    enforce();
    // Synchronous guards prevent other theme scripts from unmuting a video.
    for (const [key, value] of Object.entries({muted: true, defaultMuted: true, volume: 0, controls: false})) {
      Object.defineProperty(video, key, {
        configurable: false,
        get: () => native[key].get.call(video),
        set: () => { if (native[key].get.call(video) !== value) native[key].set.call(video, value); }
      });
    }
    video.setAttribute('controlslist', 'nodownload nofullscreen noremoteplayback');
    video.setAttribute('x-webkit-airplay', 'deny');
    video.setAttribute('disablepictureinpicture', '');
    video.setAttribute('disableremoteplayback', '');
    video.tabIndex = -1;
    video.addEventListener('contextmenu', event => event.preventDefault());
    for (const event of ['volumechange', 'play', 'playing', 'pause', 'ended', 'loadedmetadata', 'emptied']) {
      video.addEventListener(event, enforce);
    }
    new MutationObserver(enforce).observe(video, {attributes: true, attributeFilter: ['controls', 'muted', 'playsinline']});
    video.classList.add('ci-silent-video');
  }

  const selector = 'ci-demo-video, deferred-media[data-ci-storefront-video]';
  const controllers = new Map();
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');

  class StorefrontVideo {
    constructor(host) {
      this.host = host;
      this.visible = false;
      this.stopped = false;
      this.manual = false;
      this.generation = 0;
      this.retries = 0;
      this.abort = new AbortController();
      this.productScope = host.closest('.ci-course-product');
      this.posterHandoff = host.hasAttribute('data-ci-poster-handoff');
      this.playing = false;
      if (this.productScope) {
        const gallery = host.closest('media-gallery');
        const modal = host.closest('product-modal');
        if (gallery || modal) {
          this.visibilityObserver = new MutationObserver(() => this.sync());
          if (gallery) this.visibilityObserver.observe(gallery, {attributes: true, subtree: true, attributeFilter: ['class']});
          if (modal) this.visibilityObserver.observe(modal, {attributes: true, attributeFilter: ['open']});
        }
      }
      this.bindVideo();
      this.observer = new IntersectionObserver(entries => {
        const entry = entries[entries.length - 1];
        const visible = entry.isIntersecting && entry.intersectionRect.height >= Math.min(100, entry.boundingClientRect.height * .2) && entry.intersectionRect.width >= Math.min(80, entry.boundingClientRect.width * .2);
        if (visible !== this.visible) {
          this.visible = visible;
          this.blocked = false;
          this.retries = 0;
        }
        this.sync();
      }, {threshold: [0, .05, .1, .2, .4, .6, .8, 1]});
      this.observer.observe(host);
    }
    bindVideo() {
      const video = this.host.querySelector('video');
      if (!video || video === this.video) return;
      this.video = video;
      protect(video);
      video.loop = true;
      video.autoplay = false;
      const signal = this.abort.signal;
      if (video.dataset.poster) {
        this.posterObserver = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) {
            video.poster = video.dataset.poster;
            this.posterObserver.disconnect();
          }
        }, {rootMargin: '300px 0px'});
        this.posterObserver.observe(this.host);
      }
      if (this.posterHandoff) {
        video.addEventListener('playing', () => {
          if (!this.wantsPlayback() || video.paused) { this.stop(); return; }
          this.playing = true;
          this.pending = false;
          clearTimeout(this.playTimer);
          delete this.host.dataset.playbackError;
          this.blocked = false;
          const generation = this.generation;
          const showFrame = () => {
            if (generation === this.generation && this.playing && !video.paused && video.readyState >= 2 && this.wantsPlayback()) {
              this.host.setAttribute('data-ci-frame-ready', '');
            }
          };
          if (typeof video.requestVideoFrameCallback === 'function') video.requestVideoFrameCallback(showFrame);
          requestAnimationFrame(() => requestAnimationFrame(showFrame));
          this.update();
        }, {signal});
        for (const event of ['waiting', 'stalled', 'emptied', 'error', 'pause']) {
          video.addEventListener(event, () => {
            this.playing = false;
            if (!this.stopped || event !== 'pause') this.host.removeAttribute('data-ci-frame-ready');
            this.update();
          }, {signal});
        }
      }
      this.button = this.host.querySelector('.ci-demo-video__toggle, .ci-silent-toggle');
      if (!this.button) {
        this.button = document.createElement('button');
        this.button.type = 'button';
        this.button.className = 'ci-silent-toggle';
        const icon = document.createElement('span');
        icon.setAttribute('aria-hidden', 'true');
        this.button.append(icon);
        this.host.append(this.button);
      }
      this.button.addEventListener('click', () => {
        if (this.posterHandoff ? this.playing && !video.paused : this.pending || !video.paused) {
          this.stopped = true;
          this.manual = false;
          this.stop(true);
        } else this.requestManualPlay();
      }, {signal});
      ['play', 'playing', 'pause', 'ended', 'error'].forEach(event => video.addEventListener(event, () => this.update(), {signal}));
      ['loadedmetadata', 'loadeddata', 'canplay'].forEach(event => video.addEventListener(event, () => {
        if (this.posterHandoff && this.host.dataset.playbackError === 'AbortError' && video.readyState > (this.retryReadyState || 0)) {
          this.retryReadyState = video.readyState;
          this.blocked = false;
        }
        if (!this.blocked) this.sync();
      }, {signal}));
      this.update();
    }
    materialize() {
      if (this.video) return;
      const templateVideo = this.host.querySelector('template')?.content.querySelector('video');
      if (!templateVideo) return;
      const video = templateVideo.cloneNode(true);
      video.removeAttribute('autoplay');
      video.setAttribute('data-ci-storefront-video', '');
      protect(video);
      this.host.setAttribute('loaded', 'true');
      this.host.append(video);
      this.bindVideo();
    }
    wantsPlayback() {
      if (this.productScope) {
        const modal = this.host.closest('product-modal');
        const slide = this.host.closest('media-gallery .product__media-item');
        if (modal && !modal.hasAttribute('open')) return false;
        if (slide && !slide.classList.contains('is-active')) return false;
        if (getComputedStyle(this.host).visibility === 'hidden') return false;
      }
      return this.host.isConnected && this.visible && !document.hidden && !this.stopped && (!reduced.matches || this.manual);
    }
    stop(keepFrame = false) {
      clearTimeout(this.retryTimer);
      clearTimeout(this.playTimer);
      if (this.posterHandoff) {
        this.generation++;
        this.playing = false;
        if (!keepFrame) this.host.removeAttribute('data-ci-frame-ready');
      }
      if (this.pending) { this.generation++; this.pending = false; }
      if (this.video) { this.video.autoplay = false; this.video.pause(); }
      this.update();
    }
    requestManualPlay() {
      if (this.posterHandoff) {
        this.stop();
        this.pending = false;
      }
      this.stopped = false;
      this.manual = true;
      this.blocked = false;
      this.retries = 0;
      this.materialize();
      if (this.host.getBoundingClientRect().width > 0) this.visible = true;
      this.sync();
    }
    sync() {
      const eligible = this.wantsPlayback();
      if (this.posterHandoff) {
        if (eligible && !this.eligible) {
          this.blocked = false;
          this.retries = 0;
          this.retryReadyState = 0;
          const poster = this.host.querySelector('.deferred-media__poster img');
          if (poster) poster.loading = 'eager';
        }
        this.eligible = eligible;
      }
      if (!eligible) { this.stop(); return; }
      this.materialize();
      const video = this.video;
      if (!video || this.pending || !video.paused || this.blocked) return;
      if (video.dataset.poster && !video.hasAttribute('poster')) video.poster = video.dataset.poster;
      let addedSource = false;
      video.querySelectorAll('source[data-src]').forEach(source => {
        if (!source.hasAttribute('src')) { source.src = source.dataset.src; addedSource = true; }
      });
      if (video.dataset.src && !video.hasAttribute('src')) { video.src = video.dataset.src; addedSource = true; }
      if (addedSource) video.load();
      video.muted = true;
      video.loop = true;
      const generation = ++this.generation;
      this.pending = true;
      this.update();
      let playResult;
      try { playResult = video.play(); } catch (error) { playResult = Promise.reject(error); }
      if (this.posterHandoff && this.pending) {
        this.playTimer = setTimeout(() => {
          if (generation !== this.generation || !this.pending) return;
          this.stop();
          this.blocked = true;
          this.host.dataset.playbackError = 'PlaybackTimeout';
          this.update();
        }, 12000);
      }
      Promise.resolve(playResult).then(() => {
        if (generation !== this.generation) return;
        this.retries = 0;
        delete this.host.dataset.playbackError;
        if (!this.wantsPlayback()) this.stop();
      }).catch(error => {
        if (generation !== this.generation) return;
        this.host.dataset.playbackError = error.name || 'PlaybackError';
        if (this.posterHandoff) {
          video.pause();
          this.playing = false;
          this.host.removeAttribute('data-ci-frame-ready');
        }
        if (error.name === 'AbortError' && this.wantsPlayback() && this.retries < 2) {
          this.retries++;
          this.retryTimer = setTimeout(() => this.sync(), 150 * this.retries);
        } else this.blocked = true;
      }).finally(() => {
        if (generation !== this.generation) return;
        this.pending = false;
        clearTimeout(this.playTimer);
        this.update();
      });
    }
    update() {
      if (!this.button) return;
      const playing = this.posterHandoff ? this.playing && !this.video.paused : this.pending || !this.video.paused;
      if (this.posterHandoff) this.button.setAttribute('aria-busy', String(Boolean(this.pending)));
      this.button.querySelector('span').textContent = playing ? 'Ⅱ' : '▷';
      const labels = document.documentElement.dataset;
      this.button.setAttribute('aria-label', playing ? labels.videoPause : labels.videoPlay);
    }
    destroy() {
      this.stop();
      this.abort.abort();
      this.observer.disconnect();
      this.visibilityObserver?.disconnect();
      this.posterObserver?.disconnect();
    }
  }
  const attach = host => {
    if (!host || !host.matches(selector)) return;
    if (!controllers.has(host)) controllers.set(host, new StorefrontVideo(host));
    return controllers.get(host);
  };
  function mount(video) {
    const host = video.closest(selector);
    if (host) attach(host)?.bindVideo();
  }
  window.CISilentVideo = {
    protect,
    mount,
    activate(host, manual = false) {
      const controller = attach(host);
      if (manual) controller?.requestManualPlay();
      else controller?.sync();
    }
  };
  const scan = () => {
    controllers.forEach((controller, host) => {
      if (!host.isConnected) { controller.destroy(); controllers.delete(host); }
    });
    document.querySelectorAll(selector).forEach(attach);
  };
  let scanQueued = false;
  new MutationObserver(() => {
    if (scanQueued) return;
    scanQueued = true;
    queueMicrotask(() => { scanQueued = false; scan(); });
  }).observe(document.documentElement, {childList: true, subtree: true});
  document.addEventListener('visibilitychange', () => controllers.forEach(controller => controller.sync()));
  reduced.addEventListener('change', () => controllers.forEach(controller => { controller.manual = false; controller.sync(); }));
  window.addEventListener('pageshow', scan);
  scan();
})();
